import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  InputBase,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { COLORS, FONT_FAMILY, TYPOGRAPHY } from "../constants/color";
import { Icon } from "../icons";
import { useAuth } from "../context/authContext";
import axios from "axios";
import { url } from "../constants/url";
import ClientNotificationBanner from "../components/ClientNotificationBanner";

// Credit status styles
const creditStatusStyles = {
  ADDED: { bgcolor: "#8CD47E", color: "#fff" },
  USED: { bgcolor: "#FF6961", color: "#fff" },
  REFUNDED: { bgcolor: "#FFB54C", color: "#fff" },
  EXPIRED: { bgcolor: "#777777", color: "#fff" },
};

// Usage type styles
const usageTypeStyles = {
  CREDIT_PURCHASE: { bgcolor: "#4CAF50", color: "#fff" },
  CHECKLIST_REQUEST: { bgcolor: "#2196F3", color: "#fff" },
  MANUAL_ADJUSTMENT: { bgcolor: "#9C27B0", color: "#fff" },
  REFUND: { bgcolor: "#FF9800", color: "#fff" },
};

const ClientCreditHistory = () => {
  const { userData, isAuthenticated, hasRole, token } = useAuth();
  const [creditData, setCreditData] = useState([]);
  const [userCreditHistory, setUserCreditHistory] = useState([]);
  const [creditSummary, setCreditSummary] = useState(null);
  const [outstationCreditSummary, setOutstationCreditSummary] = useState(null);
  const [creditPlans, setCreditPlans] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize] = useState(12);

  const isIndividualClient = hasRole("CLIENT") && userData?.clientRole?.roleType === "Individual";

  // Modal state
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientHistoryModalOpen, setClientHistoryModalOpen] = useState(false);
  const [clientHistory, setClientHistory] = useState([]);
  const [clientDetails, setClientDetails] = useState(null);
  const [clientCreditSummary, setClientCreditSummary] = useState(null);
  const [clientOutstationCreditSummary, setClientOutstationCreditSummary] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchDebounce, setSearchDebounce] = useState("");

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounce(searchQuery);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch all credit plans to get amounts
  const fetchAllCreditPlans = useCallback(async () => {
    try {
      const response = await axios.get(`${url}/api/credits/fetch`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page: 1,
          limit: 100,
        }
      });

      const plansMap = {};
      response.data.plans.forEach(plan => {
        plansMap[plan._id] = {
          planName: plan.planName,
          credits: plan.credits,
          amount: plan.amount
        };
      });
      setCreditPlans(plansMap);
    } catch (err) {
      console.error("Error fetching credit plans:", err);
    }
  }, [token]);

  // Helper function to get display name
  const getDisplayName = (record) => {
    if (record.usageType === 'CHECKLIST_REQUEST') {
      return record.templateTitle || record.requestName || "Project";
    }
    if (record.planName === "Other Plan" && record.otherPlan) {
      return `Custom Plan (${record.otherPlan.credits} credits)`;
    }
    return record.planName || "Credit Plan";
  };

  // Helper function to get display amount
  const getDisplayAmount = (record) => {
    if (record.planName === "Other Plan" && record.otherPlan) {
      return record.otherPlan.amount || 0;
    }

    if (record.creditID && creditPlans[record.creditID]) {
      return creditPlans[record.creditID].amount;
    }

    if (record.amount !== undefined) {
      return record.amount;
    }

    return null;
  };

  // Helper function to get usage type display
  const getUsageTypeDisplay = (record) => {
    if (record.usageType === 'CHECKLIST_REQUEST') return 'PROJECT';
    if (record.usageType === 'CREDIT_PURCHASE') return 'PURCHASE';
    return record.usageType || 'OTHER';
  };

  // Fetch all clients credit data (Admin/SuperAdmin)
  const fetchAllClientsCreditData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: page,
        limit: pageSize,
        role: "CLIENT",
        excludeRoleType: "Individual",
      };

      if (searchDebounce.trim()) {
        params.search = searchDebounce.trim();
      }

      const response = await axios.get(`${url}/api/credits/data/all`, {
        headers: { Authorization: `Bearer ${token}` },
        params: params
      });

      const { data, total, page: currentPage, limit, totalPages } = response.data;
      setCreditData(data);
      setTotalRecords(total);
      setTotalPages(totalPages);
    } catch (err) {
      console.error("Error fetching all clients credit data:", err);
      setError(err.response?.data?.message || "Failed to load credit data");
      setCreditData([]);
    } finally {
      setLoading(false);
    }
  }, [token, page, pageSize, searchDebounce]);

  // Fetch single user credit data (Client)
  const fetchUserCreditData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (!userData?._id) return;

      const response = await axios.get(`${url}/api/credits/data/${userData._id}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          historyPage: page,
          historyLimit: pageSize,
        }
      });

      const { creditHistory, creditSummary, outstationCreditSummary } = response.data;
      setUserCreditHistory(creditHistory.records || []);
      setCreditSummary(creditSummary);
      setOutstationCreditSummary(outstationCreditSummary);
      setTotalRecords(creditHistory.total || 0);
      setTotalPages(creditHistory.totalPages || 1);
    } catch (err) {
      console.error("Error fetching user credit data:", err);
      setError(err.response?.data?.message || "Failed to load credit history");
      setUserCreditHistory([]);
    } finally {
      setLoading(false);
    }
  }, [token, userData, page, pageSize]);

  // Fetch detailed client history for modal
  const fetchClientDetailedHistory = async (clientId, clientData) => {
    try {
      setHistoryLoading(true);
      setClientDetails({
        name: clientData.name,
        email: clientData.email,
        phone: clientData.phone,
      });

      const response = await axios.get(`${url}/api/credits/data/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          historyPage: 1,
          historyLimit: 100,
        }
      });

      const { creditHistory, creditSummary, outstationCreditSummary } = response.data;
      const isIndividual = clientData?.clientRole?.roleType === "Individual";
      const records = creditHistory.records || [];
      setClientHistory(isIndividual ? records.filter(r => r.planType !== "OUTSTATION") : records);
      setClientCreditSummary(creditSummary);
      setClientOutstationCreditSummary(isIndividual ? null : outstationCreditSummary);
      setClientHistoryModalOpen(true);
    } catch (err) {
      console.error("Error fetching client detailed history:", err);
      alert(err.response?.data?.message || "Failed to load client history");
    } finally {
      setHistoryLoading(false);
    }
  };

  // Handle view client history
  const handleViewClientHistory = (client) => {
    setSelectedClient(client);
    fetchClientDetailedHistory(client.userId, client);
  };

  // Main useEffect for fetching data
  useEffect(() => {
    if (!isAuthenticated || !token) {
      setLoading(false);
      return;
    }

    fetchAllCreditPlans();

    if (hasRole("CLIENT")) {
      fetchUserCreditData();
    } else if (hasRole("ADMIN") || hasRole("SUPERADMIN")) {
      fetchAllClientsCreditData();
    }
  }, [isAuthenticated, token, hasRole, fetchAllClientsCreditData, fetchUserCreditData, fetchAllCreditPlans]);

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  // Calculate displayed range
  const startIndex = totalRecords > 0 ? ((page - 1) * pageSize) + 1 : 0;
  const endIndex = Math.min(page * pageSize, totalRecords);

  // Clear search
  const clearSearch = () => {
    setSearchQuery("");
    setPage(1);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = String(date.getFullYear()).slice(-2);
      return `${day}/${month}/${year}`;
    } catch (error) {
      return dateString;
    }
  };

  // Format amount
  const formatAmount = (amount) => {
    if (amount === null || amount === undefined) return "N/A";
    if (amount === 0) return "₹0";
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  // Render admin view (all clients)
  const renderAdminView = () => {
    const tableHeaders = [
      { key: "Client Info", width: "200px" },
      { key: "Total Credits", width: "120px" },
      { key: "Used Credits", width: "110px" },
      { key: "Available Credits", width: "130px" },
      { key: "History", width: "100px" },
      { key: "Status", width: "100px" },
      { key: "View", width: "80px" },
    ];

    return (
      <Box>
        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Typography sx={{ ...TYPOGRAPHY.H2, fontFamily: FONT_FAMILY, color: COLORS.PRIMARY }}>
            All Clients Credit Data
          </Typography>

          <Box sx={{ display: "flex", gap: 1, alignItems: 'center' }}>
            {/* Search Bar */}
            <Box sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexGrow: 0,
              width: { xs: 180, sm: 250, md: 280 },
              px: 2,
              py: 0.7,
              borderRadius: 1.5,
              border: `1px solid ${COLORS.PRIMARY}`,
              backgroundColor: COLORS.BACKGROUND,
              color: COLORS.PRIMARY,
            }}
            >
              <Icon name="search" sx={{ fontSize: 18, color: "currentColor" }} />
              <InputBase
                placeholder="Search clients..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
                sx={{
                  flex: 1,
                  fontFamily: FONT_FAMILY,
                  ...TYPOGRAPHY.BODY_STANDARD,
                  color: COLORS.TEXT,
                  ml: 1,
                  mt: 0.5,
                }}
              />
              {searchQuery && (
                <IconButton size="small" onClick={clearSearch} sx={{ padding: 0.5 }}>
                  <Icon name="close2" sx={{ fontSize: 14 }} color={COLORS.ERROR} />
                </IconButton>
              )}
            </Box>
          </Box>
        </Box>

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Table Container */}
        <Box sx={{
          width: '100%',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          '&::-webkit-scrollbar': {
            height: '6px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: '#f1f1f1',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: COLORS.LIGHT_SECONDARY,
            borderRadius: '4px',
          },
        }}>
          <Paper elevation={0} sx={{
            backgroundColor: "transparent",
            minWidth: '880px'
          }}>
            <TableContainer>
              <Table sx={{
                backgroundColor: "transparent",
                "& thead th": {
                  backgroundColor: "transparent",
                  padding: "8px 6px",
                },
                "& tbody td": {
                  backgroundColor: "transparent",
                  borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                  padding: "8px 6px",
                  verticalAlign: 'top',
                },
                tableLayout: 'fixed',
                width: '100%',
              }}
              >
                <TableHead>
                  <TableRow>
                    {tableHeaders.map((head) => (
                      <TableCell
                        key={head.key}
                        sx={{
                          ...TYPOGRAPHY.H4,
                          color: COLORS.PRIMARY,
                          borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                          whiteSpace: "nowrap",
                          fontSize: "0.85rem",
                          fontWeight: 600,
                          width: head.width,
                          minWidth: head.width,
                          maxWidth: head.width,
                          padding: "8px 6px",
                        }}
                      >
                        {head.key}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={tableHeaders.length} align="center" sx={{ py: 3 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : creditData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={tableHeaders.length} align="center" sx={{ py: 3 }}>
                        <Typography sx={{ color: COLORS.TEXT }}>
                          {searchQuery.trim()
                            ? `No clients found matching "${searchQuery}"`
                            : "No clients found with credit data"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    creditData.map((client) => (
                      <TableRow key={client.userId} sx={{ borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}` }}>
                        {/* Client Info Column */}
                        <TableCell sx={{
                          width: '200px',
                          minWidth: '200px',
                          maxWidth: '200px',
                          padding: "8px 6px"
                        }}>
                          <Typography
                            sx={{
                              ...TYPOGRAPHY.BODY_LARGE,
                              color: COLORS.TEXT,
                              fontSize: "0.8rem",
                              fontWeight: 600,
                              mb: 0.5
                            }}
                          >
                            {client.name}
                          </Typography>
                          <Typography
                            sx={{
                              ...TYPOGRAPHY.BODY_SMALL,
                              color: COLORS.TEXT,
                              fontSize: "0.7rem",
                              wordBreak: 'break-word',
                              lineHeight: 1.2,
                              mb: 0.5
                            }}
                          >
                            {client.email}
                          </Typography>
                          <Typography
                            sx={{
                              ...TYPOGRAPHY.BODY_SMALL,
                              color: COLORS.TEXT,
                              fontSize: "0.7rem",
                              wordBreak: 'break-word',
                              lineHeight: 1.2
                            }}
                          >
                            {client.phone || "No phone"}
                          </Typography>
                        </TableCell>

                        {/* Total Credits Column (Total ever purchased) */}
                        <TableCell sx={{
                          width: '120px',
                          minWidth: '120px',
                          maxWidth: '120px',
                          padding: "8px 6px"
                        }}>
                          <Typography
                            sx={{
                              ...TYPOGRAPHY.BODY_LARGE,
                              color: COLORS.PRIMARY,
                              fontSize: "0.9rem",
                              fontWeight: 700,
                            }}
                          >
                            {client.totalCredits + client.usedCredits}
                          </Typography>
                        </TableCell>

                        {/* Used Credits Column */}
                        <TableCell sx={{
                          width: '110px',
                          minWidth: '110px',
                          maxWidth: '110px',
                          padding: "8px 6px"
                        }}>
                          <Typography
                            sx={{
                              ...TYPOGRAPHY.BODY_LARGE,
                              color: COLORS.ERROR,
                              fontSize: "0.9rem",
                              fontWeight: 600,
                            }}
                          >
                            {client.usedCredits}
                          </Typography>
                        </TableCell>

                        {/* Available Credits Column */}
                        <TableCell sx={{
                          width: '130px',
                          minWidth: '130px',
                          maxWidth: '130px',
                          padding: "8px 6px"
                        }}>
                          <Typography
                            sx={{
                              ...TYPOGRAPHY.BODY_LARGE,
                              color: COLORS.SUCCESS,
                              fontSize: "0.9rem",
                              fontWeight: 700,
                            }}
                          >
                            {client.totalCredits}
                          </Typography>
                        </TableCell>

                        {/* History Column */}
                        <TableCell sx={{
                          width: '100px',
                          minWidth: '100px',
                          maxWidth: '100px',
                          padding: "8px 6px"
                        }}>
                          <Chip
                            label={`${client.creditHistoryCount} records`}
                            size="small"
                            sx={{
                              bgcolor: COLORS.LIGHT_PRIMARY,
                              color: COLORS.PRIMARY,
                              fontSize: "0.65rem",
                              height: 20,
                              '& .MuiChip-label': {
                                padding: '0 6px',
                              }
                            }}
                          />
                        </TableCell>

                        {/* Status Column */}
                        <TableCell sx={{
                          width: '100px',
                          minWidth: '100px',
                          maxWidth: '100px',
                          padding: "8px 6px"
                        }}>
                          <Chip
                            label={client.isApproved ? "Approved" : "Pending"}
                            size="small"
                            sx={{
                              ...(client.isApproved
                                ? { bgcolor: "#8CD47E", color: "#fff" }
                                : { bgcolor: "#FFB54C", color: "#fff" }),
                              height: 20,
                              fontSize: "0.65rem",
                              textTransform: 'capitalize',
                              '& .MuiChip-label': {
                                padding: '0 6px',
                              }
                            }}
                          />
                        </TableCell>

                        {/* View Button Column */}
                        <TableCell sx={{
                          width: '80px',
                          minWidth: '80px',
                          maxWidth: '80px',
                          padding: "8px 6px"
                        }}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleViewClientHistory(client)}
                            sx={{
                              fontSize: "0.7rem",
                              padding: "2px 8px",
                              minWidth: "60px",
                              borderColor: COLORS.PRIMARY,
                              color: COLORS.PRIMARY,
                              '&:hover': {
                                borderColor: COLORS.PRIMARY,
                                backgroundColor: COLORS.LIGHT_PRIMARY,
                              }
                            }}
                          >
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {!loading && creditData.length > 0 && (
              <Box sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 2,
                p: 2,
                mt: 2,
                width: '100%',
                overflowX: 'auto',
              }}
              >
                <Typography sx={{ fontSize: 13, color: "#777", whiteSpace: 'nowrap' }}>
                  {startIndex} - {endIndex} of {totalRecords} clients
                </Typography>

                <Box sx={{ display: "flex", gap: 1, alignItems: 'center' }}>
                  <IconButton
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1}
                    size="small"
                    sx={{ padding: '4px' }}
                  >
                    <Icon name="chevron-left" size={18} />
                  </IconButton>

                  {Array.from({ length: Math.min(totalPages, 3) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 3) {
                      pageNum = i + 1;
                    } else if (page === 1) {
                      pageNum = i + 1;
                    } else if (page === totalPages) {
                      pageNum = totalPages - 2 + i;
                    } else {
                      pageNum = page - 1 + i;
                    }

                    if (pageNum > 0 && pageNum <= totalPages) {
                      return (
                        <Button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          sx={{
                            minWidth: 24,
                            height: 24,
                            fontSize: "0.7rem",
                            bgcolor: page === pageNum ? COLORS.PRIMARY : "transparent",
                            color: page === pageNum ? "#fff" : COLORS.TEXT,
                            border: page === pageNum ? "none" : "1px solid #E1BEE7",
                            "&:hover": {
                              bgcolor: page === pageNum ? COLORS.PRIMARY : "rgba(0,0,0,0.04)"
                            },
                          }}
                        >
                          {pageNum}
                        </Button>
                      );
                    }
                    return null;
                  })}

                  <IconButton
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page === totalPages}
                    size="small"
                    sx={{ padding: '4px' }}
                  >
                    <Icon name="chevron-right" size={18} />
                  </IconButton>
                </Box>
              </Box>
            )}
          </Paper>
        </Box>

        {/* Client History Modal */}
        <Dialog
          open={clientHistoryModalOpen}
          onClose={() => setClientHistoryModalOpen(false)}
          maxWidth="lg"
          fullWidth
          scroll="paper"
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6" sx={{ color: COLORS.PRIMARY }}>
                {clientDetails?.name}'s Credit History
              </Typography>
              <IconButton onClick={() => setClientHistoryModalOpen(false)} size="small">
                <Icon name="close2" />
              </IconButton>
            </Box>
          </DialogTitle>

          <DialogContent>
            {historyLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box>
                {/* Client Summary */}
                {clientDetails && clientCreditSummary && (
                  <Box sx={{ mb: 3, p: 2, backgroundColor: COLORS.BACKGROUND, borderRadius: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                      Client Summary
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      <Box sx={{ flex: 1, minWidth: 150 }}>
                        <Typography variant="body2" sx={{ color: COLORS.TEXT_SECONDARY }}>
                          Name
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {clientDetails.name}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 150 }}>
                        <Typography variant="body2" sx={{ color: COLORS.TEXT_SECONDARY }}>
                          Email
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {clientDetails.email}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 150 }}>
                        <Typography variant="body2" sx={{ color: COLORS.TEXT_SECONDARY }}>
                          Phone
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {clientDetails.phone || "N/A"}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
                      <Box sx={{ textAlign: 'center', flex: 1, minWidth: 100 }}>
                        <Typography variant="h6" sx={{ color: COLORS.PRIMARY, fontWeight: 700 }}>
                          {(clientCreditSummary.availableCredits || 0) + (clientCreditSummary.usedCredits || 0)}
                        </Typography>
                        <Typography variant="body2" sx={{ color: COLORS.TEXT_SECONDARY }}>
                          Total Normal
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center', flex: 1, minWidth: 100 }}>
                        <Typography variant="h6" sx={{ color: COLORS.ERROR, fontWeight: 700 }}>
                          {clientCreditSummary.usedCredits || 0}
                        </Typography>
                        <Typography variant="body2" sx={{ color: COLORS.TEXT_SECONDARY }}>
                          Used Credits
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center', flex: 1, minWidth: 100 }}>
                        <Typography variant="h6" sx={{ color: COLORS.SUCCESS, fontWeight: 700 }}>
                          {clientCreditSummary.availableCredits || 0}
                        </Typography>
                        <Typography variant="body2" sx={{ color: COLORS.TEXT_SECONDARY }}>
                          Normal Avail.
                        </Typography>
                      </Box>
                      {clientOutstationCreditSummary && selectedClient?.clientRole?.roleType !== "Individual" && (
                        <Box sx={{ textAlign: 'center', flex: 1, minWidth: 100 }}>
                          <Typography variant="h6" sx={{ color: "#FF9800", fontWeight: 700 }}>
                            {clientOutstationCreditSummary.availableCredits || 0}
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#FF9800" }}>
                            Outstation Avail.
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}

                {/* History Table */}
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Credit History ({clientHistory.length} records)
                </Typography>

                {clientHistory.length === 0 ? (
                  <Typography sx={{ textAlign: 'center', color: COLORS.TEXT, p: 3 }}>
                    No credit history found
                  </Typography>
                ) : (
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, minWidth: 90 }}>Date</TableCell>
                          <TableCell sx={{ fontWeight: 600, minWidth: 140 }}>Template/Plan</TableCell>
                          <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>Type</TableCell>
                          <TableCell sx={{ fontWeight: 600, minWidth: 80 }}>Added</TableCell>
                          <TableCell sx={{ fontWeight: 600, minWidth: 80 }}>Used</TableCell>
                          <TableCell sx={{ fontWeight: 600, minWidth: 90 }}>Amount</TableCell>
                          <TableCell sx={{ fontWeight: 600, minWidth: 150 }}>Comment</TableCell>
                          <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>Usage</TableCell>
                          <TableCell sx={{ fontWeight: 600, minWidth: 80 }}>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {clientHistory.map((record, index) => {
                          const displayName = getDisplayName(record);
                          const displayAmount = getDisplayAmount(record);
                          const usageTypeDisplay = getUsageTypeDisplay(record);

                          return (
                            <TableRow key={index}>
                              <TableCell>{formatDate(record.dateAdded)}</TableCell>
                              <TableCell>{displayName}</TableCell>
                              <TableCell>
                                <Chip
                                  label={record.planType || "NORMAL"}
                                  size="small"
                                  sx={{
                                    fontSize: "0.65rem",
                                    height: 20,
                                    bgcolor: record.planType === "OUTSTATION" ? COLORS.PRIMARY : COLORS.LIGHT_PRIMARY,
                                    color: record.planType === "OUTSTATION" ? "#fff" : COLORS.PRIMARY,
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                {record.creditsAdded > 0 ? (
                                  <Chip label={`+${record.creditsAdded}`} size="small" sx={{ bgcolor: "#8CD47E", color: "#fff", fontWeight: 600 }} />
                                ) : "0"}
                              </TableCell>
                              <TableCell>
                                {record.creditsAdded < 0 ? (
                                  <Chip label={`${Math.abs(record.creditsAdded)}`} size="small" sx={{ bgcolor: "#FF6961", color: "#fff", fontWeight: 600 }} />
                                ) : "0"}
                              </TableCell>
                              <TableCell>{formatAmount(displayAmount)}</TableCell>
                              <TableCell>
                                <Typography sx={{ fontSize: '0.75rem', color: COLORS.TEXT }}>
                                  {record.adminComment || "-"}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={usageTypeDisplay}
                                  size="small"
                                  sx={{
                                    ...(usageTypeStyles[record.usageType] || { bgcolor: '#9E9E9E', color: '#fff' }),
                                    fontSize: "0.65rem",
                                    height: 20,
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                {(record.usageType === "CREDIT_PURCHASE" || record.usageType === "PLAN_ACTIVATION" || record.creditsAdded > 0) ? (
                                  <IconButton
                                    size="small"
                                    title="Download Invoice"
                                    onClick={() => window.open(`${url}/api/invoice/download/${selectedClient?.userId || selectedClient?._id}/${record._id}?token=${token}`)}
                                    sx={{ color: COLORS.PRIMARY }}
                                  >
                                    <Icon name="download" size={16} />
                                  </IconButton>
                                ) : "-"}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setClientHistoryModalOpen(false)}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  };

  // Render client view (single user)
  const renderClientView = () => {
    const tableHeaders = isIndividualClient ? [
      { key: "Date", width: "120px" },
      { key: "Plan Name", width: "220px" },
      { key: "Plan Used", width: "100px" },
      { key: "Amount", width: "130px" },
      { key: "Action", width: "90px" }
    ] : [
      { key: "Date", width: "90px" },
      { key: "Template/Plan", width: "150px" },
      { key: "Type", width: "80px" },
      { key: "Added", width: "80px" },
      { key: "Used", width: "80px" },
      { key: "Amount", width: "90px" },
      { key: "Comment", width: "130px" },
      { key: "Usage", width: "90px" },
      { key: "Action", width: "90px" }
    ];

    return (
      <Box>
        {/* Header */}
        <Box sx={{
          display: "flex",
          justify: "space-between",
          alignItems: "center",
          mb: 3,
        }}
        >
          <Typography sx={{ ...TYPOGRAPHY.H2, fontFamily: FONT_FAMILY, color: COLORS.PRIMARY }}>
            {isIndividualClient ? "Payment History" : "My Credit History"}
          </Typography>

          {/* Credit Summary */}
          {creditSummary && !isIndividualClient && (
            <Box sx={{ display: "flex", gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ textAlign: 'center', p: 1.5, backgroundColor: COLORS.BACKGROUND, borderRadius: 2, minWidth: 100 }}>
                <Typography variant="h6" sx={{ color: COLORS.PRIMARY, fontWeight: 700 }}>
                  {(creditSummary.availableCredits || 0) + (creditSummary.usedCredits || 0)}
                </Typography>
                <Typography variant="body2" sx={{ color: COLORS.TEXT_SECONDARY, fontSize: '0.75rem' }}>
                  Total Normal
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center', p: 1.5, backgroundColor: COLORS.BACKGROUND, borderRadius: 2, minWidth: 100 }}>
                <Typography variant="h6" sx={{ color: COLORS.SUCCESS, fontWeight: 700 }}>
                  {creditSummary.availableCredits || 0}
                </Typography>
                <Typography variant="body2" sx={{ color: COLORS.TEXT_SECONDARY, fontSize: '0.75rem' }}>
                  Normal Avail.
                </Typography>
              </Box>
              {outstationCreditSummary && (
                <Box sx={{ textAlign: 'center', p: 1.5, backgroundColor: "rgba(255, 152, 0, 0.1)", borderRadius: 2, minWidth: 100 }}>
                  <Typography variant="h6" sx={{ color: "#FF9800", fontWeight: 700 }}>
                    {outstationCreditSummary.availableCredits || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#FF9800", fontSize: '0.75rem' }}>
                    Outstation Avail.
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>

        {!isIndividualClient && <ClientNotificationBanner />}

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Table Container */}
        <Box sx={{
          width: '100%',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          '&::-webkit-scrollbar': {
            height: '6px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: '#f1f1f1',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: COLORS.LIGHT_SECONDARY,
            borderRadius: '4px',
          },
        }}>
          <Paper elevation={0} sx={{
            backgroundColor: "transparent",
            minWidth: isIndividualClient ? '500px' : '860px'
          }}>
            <TableContainer>
              <Table sx={{
                backgroundColor: "transparent",
                "& thead th": {
                  backgroundColor: "transparent",
                  padding: "8px 6px",
                },
                "& tbody td": {
                  backgroundColor: "transparent",
                  borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                  padding: "8px 6px",
                  verticalAlign: 'top',
                },
                tableLayout: 'fixed',
                width: '100%',
              }}
              >
                <TableHead>
                  <TableRow>
                    {tableHeaders.map((head) => (
                      <TableCell
                        key={head.key}
                        sx={{
                          ...TYPOGRAPHY.H4,
                          color: COLORS.PRIMARY,
                          borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                          whiteSpace: "nowrap",
                          fontSize: "0.85rem",
                          fontWeight: 600,
                          width: head.width,
                          minWidth: head.width,
                          maxWidth: head.width,
                          padding: "8px 6px",
                        }}
                      >
                        {head.key}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={tableHeaders.length} align="center" sx={{ py: 3 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : userCreditHistory.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={tableHeaders.length} align="center" sx={{ py: 3 }}>
                        <Typography sx={{ color: COLORS.TEXT }}>
                          No credit history found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    // Individual clients: only show purchase records, not checklist usage entries
                    (() => {
                      const displayHistory = isIndividualClient
                        ? userCreditHistory.filter(r => r.usageType === "CREDIT_PURCHASE" || !r.usageType)
                        : userCreditHistory;
                      return displayHistory.map((record, index) => {
                        const displayName = getDisplayName(record);
                        const displayAmount = getDisplayAmount(record);
                        const usageTypeDisplay = getUsageTypeDisplay(record);

                      if (isIndividualClient) {
                        const usedStatus = (() => {
                          if (!userData?.purchasedFirPlans) return "No";
                          const match = userData.purchasedFirPlans.find(plan => {
                            const recordTime = new Date(record.dateAdded).getTime();
                            const planTime = new Date(plan.purchasedAt).getTime();
                            return Math.abs(recordTime - planTime) < 5000 && plan.price === record.amount;
                          });
                          return match?.isConsumed ? "Yes" : "No";
                        })();

                        return (
                          <TableRow key={index} sx={{ borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}` }}>
                            <TableCell sx={{ width: '120px', padding: "8px 6px" }}>
                              <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.TEXT, fontSize: "0.8rem" }}>
                                {formatDate(record.dateAdded)}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ width: '220px', padding: "8px 6px" }}>
                              <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.TEXT, fontSize: "0.85rem", fontWeight: 600 }}>
                                {displayName}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ width: '100px', padding: "8px 6px" }}>
                              <Chip 
                                label={usedStatus} 
                                size="small" 
                                sx={{ 
                                  bgcolor: usedStatus === "Yes" ? "#8CD47E" : COLORS.LIGHT_SECONDARY, 
                                  color: usedStatus === "Yes" ? "#fff" : "#000",
                                  fontWeight: 600, fontSize: "0.75rem", height: 20
                                }} 
                              />
                            </TableCell>
                            <TableCell sx={{ width: '130px', padding: "8px 6px" }}>
                              <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.PRIMARY, fontSize: "0.85rem", fontWeight: 700 }}>
                                {formatAmount(displayAmount)}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ width: '90px', padding: "8px 6px" }}>
                              <IconButton
                                size="small"
                                title="Download Invoice"
                                onClick={() => window.open(`${url}/api/invoice/download/${userData._id}/${record._id}?token=${token}`)}
                                sx={{ color: COLORS.PRIMARY }}
                              >
                                <Icon name="download" size={18} />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      }

                      return (
                        <TableRow key={index} sx={{ borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}` }}>
                          <TableCell sx={{ width: '90px', padding: "8px 6px" }}>
                            <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.TEXT, fontSize: "0.75rem" }}>
                              {formatDate(record.dateAdded)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ width: '160px', padding: "8px 6px" }}>
                            <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.TEXT, fontSize: "0.8rem", fontWeight: 500 }}>
                              {displayName}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ width: '100px', padding: "8px 6px" }}>
                            <Chip
                              label={record.planType || "NORMAL"}
                              size="small"
                              sx={{
                                fontSize: "0.65rem",
                                height: 20,
                                bgcolor: record.planType === "OUTSTATION" ? COLORS.PRIMARY : COLORS.LIGHT_PRIMARY,
                                color: record.planType === "OUTSTATION" ? "#fff" : COLORS.PRIMARY,
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ width: '100px', padding: "8px 6px" }}>
                            {record.creditsAdded > 0 ? (
                              <Chip label={`+${record.creditsAdded}`} size="small" sx={{ bgcolor: "#8CD47E", color: "#fff", fontWeight: 600, fontSize: "0.80rem" }} />
                            ) : "0"}
                          </TableCell>
                          <TableCell sx={{ width: '100px', padding: "8px 6px" }}>
                            {record.creditsAdded < 0 ? (
                              <Chip label={`${Math.abs(record.creditsAdded)}`} size="small" sx={{ bgcolor: "#FF6961", color: "#fff", fontWeight: 600, fontSize: "0.80rem" }} />
                            ) : "0"}
                          </TableCell>
                          <TableCell sx={{ width: '100px', padding: "8px 6px" }}>
                            <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.WARNING, fontSize: "0.8rem", fontWeight: 600 }}>
                              {formatAmount(displayAmount)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ width: '150px', padding: "8px 6px" }}>
                            <Typography sx={{ fontSize: '0.75rem', color: COLORS.TEXT }}>
                              {record.adminComment || "-"}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ width: '90px', padding: "8px 6px" }}>
                            <Chip
                              label={usageTypeDisplay}
                              size="small"
                              sx={{
                                ...(usageTypeStyles[record.usageType] || { bgcolor: '#9E9E9E', color: '#fff' }),
                                fontSize: "0.65rem",
                                height: 20,
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ width: '90px', padding: "8px 6px" }}>
                            {(record.usageType === "CREDIT_PURCHASE" || record.usageType === "PLAN_ACTIVATION" || record.creditsAdded > 0) ? (
                              <IconButton
                                size="small"
                                title="Download Invoice"
                                onClick={() => window.open(`${url}/api/invoice/download/${userData._id}/${record._id}?token=${token}`)}
                                sx={{ color: COLORS.PRIMARY }}
                              >
                                <Icon name="download" size={18} />
                              </IconButton>
                            ) : "-"}
                          </TableCell>
                        </TableRow>
                      );
                      });
                    })()
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {!loading && userCreditHistory.length > 0 && (
              <Box sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 2,
                p: 2,
                mt: 2,
                width: '100%',
                overflowX: 'auto',
              }}
              >
                <Typography sx={{ fontSize: 13, color: "#777", whiteSpace: 'nowrap' }}>
                  {startIndex} - {endIndex} of {totalRecords} records
                </Typography>

                <Box sx={{ display: "flex", gap: 1, alignItems: 'center' }}>
                  <IconButton
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1}
                    size="small"
                    sx={{ padding: '4px' }}
                  >
                    <Icon name="chevron-left" size={18} />
                  </IconButton>

                  {Array.from({ length: Math.min(totalPages, 3) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 3) {
                      pageNum = i + 1;
                    } else if (page === 1) {
                      pageNum = i + 1;
                    } else if (page === totalPages) {
                      pageNum = totalPages - 2 + i;
                    } else {
                      pageNum = page - 1 + i;
                    }

                    if (pageNum > 0 && pageNum <= totalPages) {
                      return (
                        <Button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          sx={{
                            minWidth: 24,
                            height: 24,
                            fontSize: "0.7rem",
                            bgcolor: page === pageNum ? COLORS.PRIMARY : "transparent",
                            color: page === pageNum ? "#fff" : COLORS.TEXT,
                            border: page === pageNum ? "none" : "1px solid #E1BEE7",
                            "&:hover": {
                              bgcolor: page === pageNum ? COLORS.PRIMARY : "rgba(0,0,0,0.04)"
                            },
                          }}
                        >
                          {pageNum}
                        </Button>
                      );
                    }
                    return null;
                  })}

                  <IconButton
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page === totalPages}
                    size="small"
                    sx={{ padding: '4px' }}
                  >
                    <Icon name="chevron-right" size={18} />
                  </IconButton>
                </Box>
              </Box>
            )}
          </Paper>
        </Box>
      </Box>
    );
  };

  // Show message if user doesn't have access
  if (!isAuthenticated || !(hasRole("CLIENT") || hasRole("ADMIN") || hasRole("SUPERADMIN"))) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          This page is only accessible to CLIENT, ADMIN, or SUPERADMIN users.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {hasRole("CLIENT") ? renderClientView() : renderAdminView()}
    </Box>
  );
};

export default ClientCreditHistory;